// EEGDashboard.jsx — Device → Patient → Start EEG flow with SignalR group routing
import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import * as signalR from "@microsoft/signalr";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import FormControl from "@mui/material/FormControl";
import MuiSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

/* ---------- Config ---------- */
const API_BASE  = process.env.REACT_APP_API || "https://admin.cosmaonline.com/api";
const HUB_URL   = process.env.REACT_APP_HUB_URL || "https://test.cosmaonline.com/livehub";

const CHANNELS = [
  "fp1","f7","t3","t5","fp2","f8","t4","t6",
  "f3","fz","f4","c3","cz","c4","p3","p4"
].map(c => c.toLowerCase());

const CHANNEL_COLORS = [
  "#9E9E9E","#8E44AD","#2E86C1","#27AE60",
  "#F1C40F","#E67E22","#C0392B","#A04000",
  "#4ac0c0","#361e77","#161305","#b6ada5",
  "#a3a115","#61320b","#c7864d","#2bc076"
];

const WINDOW_MS    = 1000 * 8;
const SAMPLE_MS    = 8;
const MAX_POINTS   = 2000;
const CHANNEL_HEIGHT = 120;
const SENTINEL_MAX = 1e7;

/* ---------- Utilities ---------- */
const normalizeChannel = (c) =>
  typeof c === "string" ? c.trim().toLowerCase().replace(/\r|\n/g, "") : "value";

const computeRMS = (arr) => {
  if (!arr || !arr.length) return 0;
  let sum = 0;
  for (const p of arr) sum += p.y * p.y;
  return Math.round(Math.sqrt(sum / arr.length) * 100) / 100;
};

/* ---------- Component ---------- */
export default function EEGDashboard() {
  // ── Setup state ──
  const [orgId, setOrgId]         = useState(null);
  const [orgName, setOrgName]     = useState("");
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgError, setOrgError]   = useState(null);
  const [devices, setDevices]     = useState([]);
  const [patients, setPatients]   = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [selectedPatient, setSelectedPatient] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [setupError, setSetupError] = useState(null);
  const [hubConnected, setHubConnected] = useState(false);

  // ── Stream state ──
  const [isRunning, setIsRunning] = useState(false);
  const [showValues, setShowValues] = useState(false);
  const [alertMsg, setAlertMsg]   = useState(null);
  const [isDemoRunning, setIsDemoRunning] = useState(false);

  const dataRef     = useRef({});
  const connRef     = useRef(null);
  const tokenRef    = useRef(null);
  const demoTimerRef = useRef(null);
  const demoPhaseRef = useRef(0);

  const [seriesState, setSeriesState] = useState(() => {
    const init = {};
    for (const ch of CHANNELS) init[ch] = [{ name: ch, data: [] }];
    return init;
  });

  const showAlert = (msg, ms = 3000) => {
    setAlertMsg(msg);
    setTimeout(() => setAlertMsg(null), ms);
  };

  const getToken = () => document.cookie.split(";").map(c => c.trim())
    .find(c => c.startsWith("cosma-token="))?.split("=").slice(1).join("=");

  // ── Auto-detect orgId from JWT token after login ──
  const detectOrg = () => {
    CHANNELS.forEach(ch => { if (!dataRef.current[ch]) dataRef.current[ch] = []; });
    const token = getToken();
    tokenRef.current = token;

    if (!token) {
      setOrgLoading(false);
      setOrgError("Not logged in. Please log in first.");
      return;
    }

    setOrgLoading(true);
    setOrgError(null);

    fetch(`${API_BASE}/Device/MyOrg`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        if (data?.orgId) {
          setOrgId(data.orgId);
          setOrgName(data.orgName || "");
        } else {
          setOrgError("No organisation found for your account.");
        }
      })
      .catch(() => setOrgError("Could not detect organisation. Please try again."))
      .finally(() => setOrgLoading(false));
  };

  useEffect(() => { detectOrg(); }, []);

  // ── Load devices + patients when orgId is known ──
  const loadDevicesAndPatients = () => {
    if (!orgId) return;
    setLoadingSetup(true);
    setSetupError(null);
    Promise.all([
      fetch(`${API_BASE}/Device/GetOrgDevices?orgId=${orgId}`).then(r => r.json()),
      fetch(`${API_BASE}/Device/GetOrgPatients?orgId=${orgId}`).then(r => r.json()),
    ])
      .then(([devs, pats]) => {
        setDevices(Array.isArray(devs) ? devs : []);
        setPatients(Array.isArray(pats) ? pats : []);
        if (!devs?.length) setSetupError("No devices registered for this organisation.");
      })
      .catch(() => setSetupError("Failed to load devices and patients."))
      .finally(() => setLoadingSetup(false));
  };

  useEffect(() => { loadDevicesAndPatients(); }, [orgId]);

  // ── Build SignalR connection (not started yet) ──
  useEffect(() => {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    conn.onreconnecting(() => setHubConnected(false));
    conn.onreconnected(() => setHubConnected(true));
    conn.onclose(() => setHubConnected(false));

    conn.on("ReceiveData", (payload) => {
      try {
        const items = parsePayload(payload);
        if (items && items.length) pushParsed(items);
      } catch (err) {
        console.error("parse error", err);
      }
    });

    conn.on("Subscribed", (deviceId) => {

    });

    connRef.current = conn;
    return () => {
      try { conn.stop(); } catch (e) {}
      if (demoTimerRef.current) { clearInterval(demoTimerRef.current); demoTimerRef.current = null; }
    };
  }, []);

  /* ----- Payload parser ----- */
  const parsePayload = (payload) => {
    if (payload == null) return [];
    if (payload && typeof payload === "object" && payload.d) return parsePayload(payload.d);
    if (Array.isArray(payload)) return payload.flatMap(p => parsePayload(p));
    if (typeof payload === "string") {
      const t = payload.trim();
      if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
        try { return parsePayload(JSON.parse(t)); } catch (e) {}
      }
      const sp = t.indexOf(" ");
      if (sp === -1) {
        const vals = t.split(",").map(s => Number(s)).filter(Number.isFinite);
        return [{ channel: "value", values: vals, baseTs: Date.now() }];
      }
      const ch   = normalizeChannel(t.slice(0, sp));
      const rest = t.slice(sp + 1).replace(/^['"]|['"]$/g, "");
      const vals = rest.split(",").map(s => Number(s)).filter(Number.isFinite);
      return [{ channel: ch, values: vals, baseTs: Date.now() }];
    }
    if (typeof payload === "number") return [{ channel: "value", values: [payload], baseTs: Date.now() }];
    if (typeof payload === "object") {
      if (payload.channel && payload.values)
        return [{ channel: normalizeChannel(payload.channel), values: Array.isArray(payload.values) ? payload.values : [payload.values], baseTs: payload.ts ?? Date.now() }];
      if (payload.channels && typeof payload.channels === "object") {
        const out = []; const base = payload.ts ?? Date.now();
        for (const [k, v] of Object.entries(payload.channels))
          out.push({ channel: normalizeChannel(k), values: Array.isArray(v) ? v : [v], baseTs: base });
        return out;
      }
      const maybe = [];
      for (const [k, v] of Object.entries(payload)) {
        if (k === "ts" || k === "timestamp") continue;
        if (typeof v === "number") maybe.push({ channel: normalizeChannel(k), values: [v], baseTs: payload.ts ?? Date.now() });
        else if (Array.isArray(v)) maybe.push({ channel: normalizeChannel(k), values: v.map(n => Number(n)).filter(Number.isFinite), baseTs: payload.ts ?? Date.now() });
      }
      if (maybe.length) return maybe;
    }
    return [];
  };

  /* ----- Push parsed data to chart buffers ----- */
  const pushParsed = (items) => {
    const now = Date.now();
    for (const it of items) {
      const ch = normalizeChannel(it.channel || "value");
      if (!CHANNELS.includes(ch)) continue;
      if (!dataRef.current[ch]) dataRef.current[ch] = [];
      const base = typeof it.baseTs === "number" ? it.baseTs : now;
      const vals = Array.isArray(it.values) ? it.values : [it.values];
      for (let i = 0; i < vals.length; i++) {
        const idxFromEnd = vals.length - 1 - i;
        let ts = base - idxFromEnd * SAMPLE_MS;
        if (Math.abs(ts - now) > 1000 * 60 * 10) ts = now - idxFromEnd * SAMPLE_MS;
        const v = Number(vals[i]);
        if (!Number.isFinite(v) || Math.abs(v) >= SENTINEL_MAX) continue;
        dataRef.current[ch].push({ x: ts, y: v });
      }
      const cutoff = Date.now() - WINDOW_MS;
      dataRef.current[ch] = dataRef.current[ch].filter(p => p.x >= cutoff);
      if (dataRef.current[ch].length > MAX_POINTS) dataRef.current[ch] = dataRef.current[ch].slice(-MAX_POINTS);
      try {
        ApexCharts.exec(`chart-${ch}`, "updateSeries", [{ name: ch, data: dataRef.current[ch] }], true);
      } catch (e) {}
    }
    const snap = {};
    for (const ch of CHANNELS) snap[ch] = [{ name: ch, data: dataRef.current[ch] || [] }];
    setSeriesState(snap);
  };

  /* ----- Parse JWT to get userId ----- */
  const getUserIdFromToken = () => {
    try {
      const token = getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.sub || payload.userId || payload["custom:userId"] || null;
    } catch { return null; }
  };

  /* ----- Start: StartSession → SignalR connect + subscribe device group ----- */
  const startSession = async () => {
    // deviceId is optional (Lambda test mode); patientId falls back to logged-in user
    const deviceId  = selectedDevice || "206EF1AA3264";
    const patientId = selectedPatient || getUserIdFromToken();

    if (!patientId) {
      showAlert("Could not determine patient — please log in");
      return;
    }

    try {
      // 1. Create DB session (best-effort — don't block if it fails)
      try {
        const r = await fetch(`${API_BASE}/Device/StartSession`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenRef.current}`,
          },
          body: JSON.stringify({ deviceId, patientId }),
        });
        if (r.ok) {
          const data = await r.json();
          setSessionId(data.sessionId);
        }
      } catch (e) {
        console.warn("StartSession skipped:", e.message);
      }

      // 2. Connect SignalR and subscribe to device group
      const conn = connRef.current;
      if (conn.state === signalR.HubConnectionState.Disconnected)
        await conn.start();

      setHubConnected(true);
      await conn.invoke("SubscribeDevice", deviceId);

      setIsRunning(true);
      showAlert("EEG Started");
    } catch (err) {
      console.error("Start failed", err);
      showAlert("Start failed");
    }
  };

  /* ----- Stop: StopSession → unsubscribe → disconnect ----- */
  const stopSession = async () => {
    try {
      if (sessionId) {
        await fetch(`${API_BASE}/Device/StopSession`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });
        setSessionId(null);
      }

      const conn = connRef.current;
      if (conn && conn.state === signalR.HubConnectionState.Connected) {
        await conn.invoke("UnsubscribeDevice", selectedDevice);
        await conn.stop();
      }

      setIsRunning(false);
      showAlert("EEG Stopped");
    } catch (err) {
      console.error("Stop failed", err);
      showAlert("Stop failed");
    }
  };

  /* ----- Test inject (one-shot) ----- */
  const testAll = () => {
    const samples = 300; const base = Date.now();
    const items = CHANNELS.map((ch, idx) => {
      const freq = 5 + (idx % 6);
      const vals = Array.from({ length: samples }, (_, i) =>
        Math.round((20 + idx % 6) * Math.sin(2 * Math.PI * freq * (i * SAMPLE_MS) / 1000) + (Math.random() - 0.5) * 6));
      return { channel: ch, values: vals, baseTs: base };
    });
    pushParsed(items);
  };

  /* ----- Demo mode: continuous synthetic EEG stream ----- */
  const startDemo = () => {
    if (demoTimerRef.current) return;
    demoPhaseRef.current = 0;
    setIsDemoRunning(true);
    showAlert("Demo running");

    demoTimerRef.current = setInterval(() => {
      const base = Date.now();
      const phase = demoPhaseRef.current;
      // Each channel gets a different dominant frequency (alpha 8-13Hz, beta 13-30Hz, theta 4-8Hz)
      const freqMap = [10,10,8,8,12,12,9,9,20,20,18,18,6,6,7,7];
      const ampMap  = [25,22,18,18,28,26,20,20,15,15,18,18,12,12,14,14];
      const items = CHANNELS.map((ch, idx) => {
        const freq = freqMap[idx];
        const amp  = ampMap[idx];
        const vals = Array.from({ length: 50 }, (_, i) => {
          const t = (phase * 50 + i) * SAMPLE_MS / 1000;
          // dominant wave + harmonic + noise
          return Math.round(
            amp * Math.sin(2 * Math.PI * freq * t) +
            (amp * 0.3) * Math.sin(2 * Math.PI * (freq * 2) * t) +
            (Math.random() - 0.5) * 8
          );
        });
        return { channel: ch, values: vals, baseTs: base };
      });
      demoPhaseRef.current += 1;
      pushParsed(items);
    }, 400);
  };

  const stopDemo = () => {
    if (demoTimerRef.current) {
      clearInterval(demoTimerRef.current);
      demoTimerRef.current = null;
    }
    setIsDemoRunning(false);
    showAlert("Demo stopped");
  };

  /* ----- Chart options ----- */
  const optionsFor = (ch, hideXAxis = false) => ({
    chart: { id: `chart-${ch}`, animations: { enabled: false }, toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 1.2 },
    grid: { borderColor: "#f0f0f0", xaxis: { lines: { show: true } } },
    xaxis: { type: "datetime", range: WINDOW_MS, labels: { show: !hideXAxis, datetimeUTC: false, style: { fontSize: "12px" } } },
    yaxis: { show: false },
    tooltip: { enabled: true, y: { formatter: (val) => val != null ? val.toFixed(2) : val } },
    markers: { size: showValues ? 3 : 0 },
    dataLabels: {
      enabled: showValues,
      formatter: (val) => val != null ? Number(val).toFixed(2) : "",
      offsetY: -8,
      style: { fontSize: "10px", colors: ["#222"] },
    },
  });

  /* ----- Channel row ----- */
  const ChannelRow = ({ ch, idx }) => {
    const s      = seriesState[ch] || [{ name: ch, data: [] }];
    const buf    = dataRef.current[ch] || [];
    const rms    = computeRMS(buf);
    const top    = buf.length ? Math.round(Math.max(...buf.map(p => p.y))) : 0;
    const bottom = buf.length ? Math.round(Math.min(...buf.map(p => p.y))) : 0;
    const color  = CHANNEL_COLORS[idx % CHANNEL_COLORS.length];
    return (
      <MDBox display="flex" alignItems="stretch" sx={{ borderBottom: "1px solid #eef0f4", bgcolor: "white" }}>
        <MDBox width="92px" display="flex" flexDirection="column" alignItems="center" justifyContent="space-between" p={1}>
          <MDBox width="36px" height="36px" borderRadius="50%" display="flex" alignItems="center" justifyContent="center"
            shadow="sm" sx={{ background: color, color: "#fff", fontWeight: 700, fontSize: 12 }}>
            {ch.toUpperCase()}
          </MDBox>
          <MDTypography variant="caption" fontWeight="medium" sx={{ mt: 0.5 }}>{`+${top}µV`}</MDTypography>
          <MDTypography variant="caption" color="text">{`${bottom}µV`}</MDTypography>
        </MDBox>
        <MDBox flex={1} minWidth={0}>
          <MDBox sx={{ height: CHANNEL_HEIGHT }}>
            <ReactApexChart options={optionsFor(ch, idx !== CHANNELS.length - 1)} series={s} type="line" height={CHANNEL_HEIGHT} />
          </MDBox>
        </MDBox>
        <MDBox width="100px" display="flex" flexDirection="column" justifyContent="center" p={1}>
          <MDTypography variant="caption" color="text" textAlign="right">{rms} µVrms</MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  const alertColor = alertMsg?.includes("fail") || alertMsg?.includes("failed") ? "error"
    : alertMsg === "EEG Stopped" ? "warning" : "success";

  const selectedPatientName = patients.find(p => p.userId === selectedPatient);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* ── Org detection banner ── */}
        <MDBox mb={2}>
          {orgLoading && (
            <MDBox display="flex" alignItems="center" gap={1} px={1}>
              <CircularProgress size={16} />
              <MDTypography variant="caption" color="text">Detecting your organisation…</MDTypography>
            </MDBox>
          )}
          {orgError && (
            <MDBox display="flex" alignItems="center" gap={2} px={1} py={1}
              sx={{ background: "#fff3f3", borderRadius: 2, border: "1px solid #fcc" }}>
              <Icon sx={{ color: "error.main" }}>error_outline</Icon>
              <MDTypography variant="caption" color="error">{orgError}</MDTypography>
              <MDButton size="small" color="error" variant="outlined" onClick={detectOrg} sx={{ ml: 1 }}>
                Retry
              </MDButton>
            </MDBox>
          )}
          {orgId && !orgLoading && (
            <MDBox display="flex" alignItems="center" gap={1} px={1} py={0.5}
              sx={{ background: "#f0fff4", borderRadius: 2, border: "1px solid #b7ebc9" }}>
              <Icon sx={{ color: "success.main", fontSize: 18 }}>business</Icon>
              <MDTypography variant="caption" color="text">
                Organisation: <strong>{orgName || `Org #${orgId}`}</strong>
              </MDTypography>
              <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: hubConnected ? "success.main" : "grey.400", ml: 2 }} />
              <MDTypography variant="caption" color="text">{hubConnected ? "Hub Connected" : "Hub Idle"}</MDTypography>
            </MDBox>
          )}
        </MDBox>

        {/* ── Step 1+2: Select device + patient ── */}
        <Card sx={{ mb: 3 }}>
          <MDBox display="flex" alignItems="center" px={3} pt={3} pb={1} gap={1}>
            <Icon sx={{ color: "info.main" }}>sensors</Icon>
            <MDTypography variant="h6">EEG Session Setup</MDTypography>
            {alertMsg && (
              <MDBox bgColor={alertColor} borderRadius="md" px={2} py={0.5} ml={2}>
                <MDTypography variant="button" color="white" fontWeight="bold">{alertMsg}</MDTypography>
              </MDBox>
            )}
          </MDBox>

          <MDBox px={4} pb={3}>
            {orgLoading ? (
              <MDBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <MDTypography variant="caption" color="text">Detecting organisation…</MDTypography>
              </MDBox>
            ) : orgError ? (
              <MDBox>
                <MDTypography variant="caption" color="error" display="block" mb={1}>
                  No organisation linked — device selection unavailable. You can still run the demo below.
                </MDTypography>
                <MDButton
                  color="warning" size="small"
                  onClick={isDemoRunning ? stopDemo : startDemo}
                  variant={isDemoRunning ? "contained" : "outlined"}
                >
                  <Icon sx={{ mr: 0.5 }}>{isDemoRunning ? "stop" : "science"}</Icon>
                  {isDemoRunning ? "Stop Demo" : "Start Demo"}
                </MDButton>
              </MDBox>
            ) : loadingSetup ? (
              <MDBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <MDTypography variant="caption" color="text">Loading devices &amp; patients…</MDTypography>
              </MDBox>
            ) : (
              <>
                {setupError && (
                  <MDBox display="flex" alignItems="center" gap={2} mb={2} px={1} py={1}
                    sx={{ background: "#fff8e1", borderRadius: 2, border: "1px solid #ffe082" }}>
                    <Icon sx={{ color: "warning.main" }}>warning_amber</Icon>
                    <MDTypography variant="caption" color="warning">{setupError}</MDTypography>
                    <MDButton size="small" color="warning" variant="outlined" onClick={loadDevicesAndPatients} sx={{ ml: 1 }}>
                      Retry
                    </MDButton>
                  </MDBox>
                )}

                <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={2}>

                  {/* Device selector */}
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.5}>
                      Device ({devices.length})
                    </MDTypography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <MuiSelect
                        value={selectedDevice}
                        onChange={e => setSelectedDevice(e.target.value)}
                        displayEmpty
                        disabled={isRunning}
                        sx={{ fontSize: 14 }}
                      >
                        <MenuItem value=""><em>Select device…</em></MenuItem>
                        {devices.map(d => (
                          <MenuItem key={d.deviceId} value={d.deviceId}>
                            {d.name || d.deviceId}
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  </MDBox>

                  {/* Patient selector */}
                  <MDBox>
                    <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.5}>
                      Patient ({patients.length})
                    </MDTypography>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <MuiSelect
                        value={selectedPatient}
                        onChange={e => setSelectedPatient(e.target.value)}
                        displayEmpty
                        disabled={isRunning}
                        sx={{ fontSize: 14 }}
                      >
                        <MenuItem value=""><em>Select patient…</em></MenuItem>
                        {patients.map(p => (
                          <MenuItem key={p.userId} value={p.userId}>
                            {p.firstName} {p.lastName} — {p.email}
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  </MDBox>

                  {/* Start / Stop */}
                  <MDBox display="flex" gap={1} mt={2.5} flexWrap="wrap">
                    <MDButton
                      color="success" size="small"
                      onClick={startSession}
                      disabled={isRunning || isDemoRunning || !selectedDevice || !selectedPatient}
                    >
                      <Icon sx={{ mr: 0.5 }}>play_arrow</Icon> Start EEG
                    </MDButton>
                    <MDButton
                      color="error" size="small"
                      onClick={stopSession}
                      disabled={!isRunning}
                    >
                      <Icon sx={{ mr: 0.5 }}>stop</Icon> Stop
                    </MDButton>

                    {/* Demo mode — no device needed */}
                    <MDBox sx={{ borderLeft: "1px solid #e0e0e0", pl: 1, display: "flex", gap: 1 }}>
                      <MDButton
                        color="warning" size="small"
                        onClick={isDemoRunning ? stopDemo : startDemo}
                        disabled={isRunning}
                        variant={isDemoRunning ? "contained" : "outlined"}
                      >
                        <Icon sx={{ mr: 0.5 }}>{isDemoRunning ? "stop" : "science"}</Icon>
                        {isDemoRunning ? "Stop Demo" : "Start Demo"}
                      </MDButton>
                    </MDBox>

                    <MDButton color="secondary" variant="outlined" size="small"
                      onClick={loadDevicesAndPatients} disabled={isRunning || loadingSetup}>
                      <Icon sx={{ mr: 0.5 }}>refresh</Icon> Refresh
                    </MDButton>
                    <FormControlLabel
                      sx={{ ml: 1 }}
                      control={<Checkbox checked={showValues} onChange={e => setShowValues(e.target.checked)} color="info" />}
                      label={<MDTypography variant="button" fontWeight="regular">Show values</MDTypography>}
                    />
                  </MDBox>

                </MDBox>
              </>
            )}

            {/* Active session info */}
            {isRunning && (
              <MDBox mt={2} display="flex" gap={2} alignItems="center"
                sx={{ background: "#f0fff4", borderRadius: 2, px: 2, py: 1 }}>
                <MDBox sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "success.main",
                  boxShadow: "0 0 0 0 rgba(0,200,83,1)",
                  animation: "pulse 1.5s infinite" }} />
                <MDTypography variant="caption" color="text">
                  <strong>LIVE</strong> — Device: <strong>{selectedDevice}</strong>
                  {selectedPatientName && <> &nbsp;|&nbsp; Patient: <strong>{selectedPatientName.firstName} {selectedPatientName.lastName}</strong></>}
                  {sessionId && <> &nbsp;|&nbsp; Session: <code style={{ fontSize: 10 }}>{sessionId}</code></>}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* ── EEG Charts ── */}
        <Card sx={{ width: "100%", overflow: "hidden" }}>
          {CHANNELS.map((ch, idx) => <ChannelRow key={ch} ch={ch} idx={idx} />)}
        </Card>

      </MDBox>
    </DashboardLayout>
  );
}
